import useOnScreen from '@/hooks/useOnScreen';
import { Box, BoxProps, Stack, StackProps } from '@mui/material';
import CN from 'classnames';
import _debounce from 'lodash/debounce';
import React, { ReactNode, useState } from 'react';

interface IProps {
  children: ReactNode[];
  autoplay?: boolean;
  uniqueId: string;
  bullets?: boolean;
  containerProps?: StackProps;
  itemsProps?: BoxProps;
  initialActiveIndex?: number;
}

export type SliderContentProps = IProps;

/**
 * SliderComponent is displayed in minimized way.
 *
 *
 * ```typescript
 *  import { SlideCard } from '@rabex-kit/core';
 *  ```
 * @param params
 * @returns
 */
const delay = 3000;

const SliderContent: React.FC<SliderContentProps> = ({
  children,
  uniqueId,
  containerProps,
  itemsProps,
  bullets = true,
  autoplay = true,
  initialActiveIndex = 0
}) => {
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const isOnScreen = useOnScreen(rootRef, { threshold: 1.0 });
  const [activeIndex, setActiveIndex] = useState(initialActiveIndex);
  const timeoutRef = React.useRef<number | undefined>();

  function resetTimeout() {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }

  React.useEffect(() => {
    if (autoplay && isOnScreen) {
      resetTimeout();
      timeoutRef.current = setTimeout(() => {
        const newIndex =
          activeIndex === children.length - 1 ? 0 : activeIndex + 1;
        scrollIntoView(newIndex);
        if (!bullets) {
          setActiveIndex(newIndex);
        }
      }, delay) as unknown as number;
    }
    return () => {
      resetTimeout();
    };
  }, [activeIndex, isOnScreen]);

  const scrollIntoView = (index: number) => {
    const elements = document.querySelectorAll(`.${uniqueId}-carousel > *`);
    if (elements) {
      elements[index]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const handleIndicator = (elements: any, currentIndex: number) => {
    for (let i = 0; i < elements.length; i++) {
      if (i === activeIndex) {
        setActiveIndex(currentIndex);
      }
    }
  };

  const handleCarousel = () => {
    const carousel = document.querySelector(`.${uniqueId}-carousel`);
    const elements = document.querySelectorAll(`.${uniqueId}-carousel > *`);

    const observer = new IntersectionObserver(
      function (entries: any) {
        // find the entry with the largest intersection ratio
        const activated = entries.reduce(function (max: any, entry: any) {
          return entry.intersectionRatio > max.intersectionRatio ? entry : max;
        });
        if (activated.intersectionRatio > 0 && bullets) {
          handleIndicator(
            elements,
            //@ts-ignore
            elementIndices[activated.target.getAttribute('id') || 'id']
          );
        }
      },
      {
        root: carousel,
        threshold: 1
      }
    );
    const elementIndices = {};
    for (let i = 0; i < elements.length; i++) {
      // @ts-ignore
      elementIndices[elements[i].getAttribute('id') || 'id'] = i;
      observer.observe(elements[i]);
    }
  };

  const { sx: containerSx, ...otherContainer } = containerProps || {};
  const {
    sx: itemSx,
    className: itemClassName,
    ...otherItem
  } = itemsProps || {};
  return (
    <Stack gap={2} overflow="hidden">
      <Stack
        ref={rootRef}
        className={`${uniqueId}-carousel`}
        gap={2}
        flexDirection="row"
        flexWrap="nowrap"
        sx={{
          overflowX: 'scroll',
          scrollSnapStop: 'always',
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          ...containerSx
        }}
        {...otherContainer}
        onScroll={() => {
          _debounce(
            () => {
              handleCarousel();
            },
            1000,
            { maxWait: 1000 }
          )();
        }}
      >
        {children.map((item: any, index: any) => (
          <Box
            key={index}
            id={`${uniqueId}-item-${index}`}
            className={CN(itemClassName || '', {
              active: index === activeIndex
            })}
            sx={{
              flexShrink: 0,
              scrollSnapAlign: 'center',
              scrollSnapStop: 'always',
              ...itemSx
            }}
            {...otherItem}
          >
            {item}
          </Box>
        ))}
      </Stack>
      {bullets && (
        <Stack flexDirection="row" justifyContent="center" gap={2} p={4}>
          {children.map((_: any, index: any) => (
            <Box
              onClick={() => {
                scrollIntoView(index);
              }}
              key={index}
              sx={(theme: any) => ({
                cursor: 'pointer',
                width: '12px',
                height: '12px',
                filter:
                  activeIndex === index
                    ? 'drop-shadow(0px 0px 1px rgba(0, 0, 0, 0.40)) drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))'
                    : 'unset',
                backgroundColor:
                  activeIndex === index
                    ? theme.palette.primary.A100
                    : '#D9D9D9',
                borderRadius: '50%',
                transition: 'all 0.2s',
                transform: activeIndex === index ? 'scale(1.25)' : 'scale(1)'
              })}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default SliderContent;
